/**
 * O que faz: tipos e textos em português dos agentes (marcos do funil, etiquetas, motivos e itens de revisão).
 * Onde usa: aba Agentes, sino de avisos, histórico de etapas do lead e badge do Pipeline.
 * Conexões: espelha os códigos gravados pelo worker (supabase/functions/_shared/lead-classification/contracts.ts).
 * Tabelas: nenhuma; só converte códigos fechados em texto de tela.
 * Data: 25/09/2026.
 */

export const MARCOS_FUNIL = [
  'novo', 'em_conversa', 'qualificado', 'negociando', 'agendado', 'compareceu', 'convertido', 'perdido',
] as const;
export type MarcoFunil = typeof MARCOS_FUNIL[number];
export type CategoriaClassificacao = MarcoFunil | 'indefinido';
export type EtiquetaSituacao = 'aguardando_equipe' | 'aguardando_lead' | 'esfriou' | 'precisa_revisao';
export type ModoRota = 'suggestion' | 'automatic';

// Descrições curtas: a clínica precisa entender o marco para ligar à coluna certa sem conhecer a IA.
export const TEXTO_MARCO: Record<CategoriaClassificacao, { rotulo: string; descricao: string; soSistema?: boolean }> = {
  novo: { rotulo: 'Novo', descricao: 'Chegou e ninguém conversou ainda.', soSistema: true },
  em_conversa: { rotulo: 'Em conversa', descricao: 'Trocou mensagens, sem interesse claro ainda.' },
  qualificado: { rotulo: 'Qualificado', descricao: 'Pediu um serviço que a clínica oferece.' },
  negociando: { rotulo: 'Negociando', descricao: 'Pediu preço ou orçamento e está decidindo.' },
  agendado: { rotulo: 'Agendado', descricao: 'Combinou dia e horário, ou tem consulta marcada no CRM.' },
  compareceu: { rotulo: 'Compareceu', descricao: 'A consulta marcada já aconteceu.', soSistema: true },
  convertido: { rotulo: 'Convertido', descricao: 'Tem venda registrada no CRM.', soSistema: true },
  perdido: { rotulo: 'Perdido', descricao: 'Desistiu ou procura um serviço que a clínica não oferece.' },
  indefinido: { rotulo: 'Precisa de revisão', descricao: 'A conversa não permite uma decisão segura.' },
};

export const TEXTO_ETIQUETA: Record<EtiquetaSituacao, string> = {
  aguardando_equipe: 'Aguardando equipe',
  aguardando_lead: 'Aguardando lead',
  esfriou: 'Sem contato recente',
  precisa_revisao: 'Precisa de revisão',
};

export const TEXTO_EXECUTOR: Record<string, string> = {
  humano: 'Pessoa',
  ia: 'IA',
  automacao: 'Automação',
  desconhecido: 'Origem não identificada',
};

export const TEXTO_AGENTE: Record<string, string> = {
  classificador_conversas: 'Classificador de conversas',
  classificador_retornos: 'Classificador de retornos',
};

export interface ConfiguracaoAgentes {
  enabled: boolean;
  iaHabilitada: boolean;
  agenteConversasAtivo: boolean;
  agenteRetornoAtivo: boolean;
  agenteRetornoModo: ModoRota;
  prazoRetornoPadraoDias: number;
  inactiveAfterDays: number;
  limiteEsperaMinutos: number;
  // Autorização explícita da clínica para a IA mover sozinha para colunas de venda ou com evento Meta.
  iaPodeMoverColunasMeta: boolean;
}

export const CONFIGURACAO_PADRAO: ConfiguracaoAgentes = {
  enabled: false,
  iaHabilitada: false,
  agenteConversasAtivo: false,
  agenteRetornoAtivo: false,
  agenteRetornoModo: 'suggestion',
  prazoRetornoPadraoDias: 30,
  inactiveAfterDays: 7,
  limiteEsperaMinutos: 30,
  iaPodeMoverColunasMeta: false,
};

export interface RotaMarco {
  categoria: CategoriaClassificacao;
  etapaId: string;
  modo: ModoRota;
}

export interface ItemRevisao {
  tipo: 'retorno' | 'etapa';
  itemId: string | null;
  leadId: string;
  leadNome: string | null;
  etapaAtual: string | null;
  etapaSugerida: string | null;
  marco: CategoriaClassificacao | null;
  motivo: string | null;
  confianca: number | null;
  agendarPara: string | null;
  dataEstimada: boolean | null;
  criadoEm: string;
  // Organização personalizada (fase 3): sugestão vem de uma regra de coluna, não do mapa de marcos.
  etapaSugeridaId: string | null;
  personalizada: boolean;
  // Critério confirmado da coluna ("Vamos usar esta coluna quando…"); nulo se a regra foi removida depois.
  criterio: string | null;
  regraVersao: string | null;
  // Data em que o card entrou na coluna atual: a correção só vale sobre o card que a pessoa viu.
  etapaAlteradaEm: string | null;
}

export interface AvisosAgentes {
  ativo: boolean;
  aguardandoEquipe: number;
  aguardandoEquipeAtrasados: number;
  retornosParaRevisar: number;
  etapasParaRevisar: number;
  limiteEsperaMinutos: number | null;
}

export interface MovimentoEtapa {
  id: string;
  etapaOrigemId: string | null;
  etapaDestinoId: string | null;
  ocorridoEm: string;
  executor: 'humano' | 'ia' | 'automacao' | 'desconhecido';
  executorOrigem: 'interno' | 'externo' | null;
  usuarioId: string | null;
  agenteChave: string | null;
  canal: string;
  motivoCodigo: string | null;
  confianca: number | null;
  modelo: string | null;
}

// Erros das RPCs chegam como códigos; a tela mostra frases em português.
const MENSAGENS_ERRO: Record<string, string> = {
  plano_sem_agentes: 'Os agentes estão disponíveis nos planos PMK e Premium.',
  retorno_exige_ia: 'Para usar o Classificador de retornos, ligue a leitura das conversas por IA.',
  sem_permissao_agentes: 'Você não tem permissão para configurar os agentes desta clínica.',
  configuracao_agentes_invalida: 'Revise os prazos: algum valor está fora do permitido.',
  destino_automatico_nao_seguro: 'Esta coluna não aceita movimento automático. Coluna de retorno é sempre só sugestão; colunas de venda ou com evento Meta exigem a autorização da clínica.',
  agentes_nao_configurados: 'Salve a configuração dos agentes antes de mudar esta autorização.',
  lead_return_must_be_future: 'A data do retorno precisa ser no futuro. Ajuste a data e tente de novo.',
  sugestao_retorno_indisponivel: 'Esta sugestão já foi decidida. Atualize a lista.',
  sugestao_etapa_indisponivel: 'Esta sugestão já foi decidida. Atualize a lista.',
  movimento_ja_superado: 'O card já foi movido depois disso; não dá para desfazer este movimento.',
  movimento_nao_desfazivel: 'Só é possível desfazer movimentos feitos pela IA.',
  sem_permissao_desfazer: 'Você não tem permissão para desfazer este movimento.',
};

export function mensagemErroAgentes(erro: unknown, padrao: string): string {
  const texto = erro instanceof Error ? erro.message : typeof erro === 'object' && erro && 'message' in erro
    ? String((erro as { message: unknown }).message) : '';
  const codigo = Object.keys(MENSAGENS_ERRO).find((chave) => texto.includes(chave));
  return codigo ? MENSAGENS_ERRO[codigo] : padrao;
}
